"use client";
import React, { useEffect, useRef } from "react";
import { NavLogo } from "./assets";
import { mustBeSubmenu } from "./helper";
import { BottomNavigationItem, BottomNavigationSubMenu, TopNavigationSubMenu } from "./items";
import {
	Breakpoint,
	DropShadowWeight,
	FontWeight,
	IBottomItem,
	ITopItem,
	ItemsGap,
	TextSize,
} from "./types";

export interface IDesktopNavBarProps {
	readonly topItems?: ReadonlyArray<ITopItem>;
	readonly bottomItems?: ReadonlyArray<IBottomItem>;
	readonly siteIdentityImageUrl: string;
	readonly siteIdentityLinkTooltip: string;
	readonly textSize: TextSize;
	readonly mobileBreakpoint: Breakpoint;
	readonly bottomBarItemsGap: ItemsGap;
	readonly itemsFontWeight: FontWeight;
	readonly belowBarDropShadowWeight: DropShadowWeight;
	readonly transparentUntilScroll?: boolean;
	readonly bottomBarHoverUnderline: boolean;
}

const DesktopNavBar: React.FC<IDesktopNavBarProps> = ({
	topItems,
	bottomItems,
	siteIdentityImageUrl,
	siteIdentityLinkTooltip,
	textSize,
	mobileBreakpoint,
	bottomBarItemsGap,
	itemsFontWeight,
	belowBarDropShadowWeight,
	transparentUntilScroll,
	bottomBarHoverUnderline,
}) => {
	const bottomBarRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		function handleScroll() {
			if (!transparentUntilScroll) {
				return;
			}
			if (
				window.scrollY > 140 &&
				!bottomBarRef.current?.classList.contains("include-background-color")
			) {
				bottomBarRef.current?.classList.add("include-background-color");
			} else if (
				window.scrollY <= 140 &&
				bottomBarRef.current?.classList.contains("include-background-color")
			) {
				bottomBarRef.current?.classList.remove("include-background-color");
			}
		}
		window.addEventListener("scroll", handleScroll);
		handleScroll();
		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, [bottomBarRef, transparentUntilScroll]);
	const desktopBottomItems = bottomItems?.filter(({ allowedScreenSizeMode }) => {
		return (
			allowedScreenSizeMode === "desktop" ||
			allowedScreenSizeMode === "any" ||
			allowedScreenSizeMode === undefined
		);
	});
	let contentBelowTopPadding = "";
	if (topItems && topItems.length > 0) {
		if (desktopBottomItems && desktopBottomItems.length > 0) {
			contentBelowTopPadding = "epic-header-mt-[102px]";
		} else {
			contentBelowTopPadding = "epic-header-mt-[36px]";
		}
	} else if (desktopBottomItems && desktopBottomItems.length > 0) {
		contentBelowTopPadding = "epic-header-mt-[66px]";
	} else {
		contentBelowTopPadding = "epic-header-mt-[0px]";
	}

	const blockAtBreakpoint =
		mobileBreakpoint === Breakpoint.xl
			? "xl:epic-header-block"
			: mobileBreakpoint === Breakpoint.lg
				? "lg:epic-header-block"
				: "md:epic-header-block";

	return (
		<>
			<nav
				className={`epic-header-hidden epic-header-fixed epic-header-z-10 epic-header-w-full ${transparentUntilScroll ? "epic-header-bg-transparent" : ""} ${blockAtBreakpoint}`}
				id="navbar_desktop"
			>
				{topItems && topItems.length > 0 ? (
					<div
						className={`epic-header-relative epic-header-z-30 epic-header-bg-[color:var(--epic-nav-top-bar-background-color)] ${
							belowBarDropShadowWeight === DropShadowWeight.Heavy
								? "drop-shadow-[0_4px_10px_rgba(0,0,0,0.25)]"
								: "drop-shadow-[0_4px_3px_rgba(0,0,0,0.25)]"
						}`}
					>
						<div className="epic-header-mx-auto epic-header-max-w-screen-xl">
							<ul className="epic-header-flex epic-header-flex-row epic-header-gap-4 epic-header-justify-center epic-header-items-stretch epic-header-h-9">
								{topItems
									? topItems.map(topItem => {
											if (topItem.subItems) {
												return (
													<TopNavigationSubMenu
														item={topItem}
														key={`nav_bar_top_navigation_submenu_${topItem.navLogoKind}`}
													/>
												);
											} else if (!mustBeSubmenu(topItem.navLogoKind)) {
												return (
													<NavLogo
														key={`nav_bar_full_logo_${topItem.navLogoKind}`}
														navLogoKind={topItem.navLogoKind}
														selected={topItem.selected}
														tooltip={topItem.tooltip}
													/>
												);
											}
										})
									: null}
							</ul>
						</div>
					</div>
				) : null}
				{desktopBottomItems && desktopBottomItems.length > 0 ? (
					<div
						className={`epic-header-relative epic-header-z-20 epic-header-font-title  ${
							itemsFontWeight === FontWeight.ExtraBold
								? "epic-header-font-extrabold"
								: itemsFontWeight === FontWeight.SemiBold
									? "epic-header-font-semibold"
									: "epic-header-font-light"
						} ${transparentUntilScroll ? "transition-colors duration-300 ease-in-out opacity-[.98] [&.include-background-color]:epic-header-bg-[color:var(--epic-nav-bottom-bar-back-color)]" : "epic-header-bg-[color:var(--epic-nav-bottom-bar-back-color)]"} ${
							belowBarDropShadowWeight === DropShadowWeight.Heavy
								? "epic-header-drop-shadow-[0_4px_10px_rgba(0,0,0,0.25)]"
								: "epic-header-drop-shadow-[0_4px_3px_rgba(0,0,0,0.25)]"
						} ${
							textSize === TextSize.Large
								? `epic-header-text-sm ${
										mobileBreakpoint === Breakpoint.xl
											? "xl:epic-header-text-xl"
											: mobileBreakpoint === Breakpoint.lg
												? "lg:epic-header-text-xl"
												: "md:epic-header-text-xl"
									}`
								: textSize === TextSize.Medium
									? `epic-header-text-xs ${
											mobileBreakpoint === Breakpoint.xl
												? "xl:epic-header-text-lg"
												: mobileBreakpoint === Breakpoint.lg
													? "lg:epic-header-text-lg"
													: "md:epic-header-text-lg"
										}`
									: `epic-header-text-xs ${
											mobileBreakpoint === Breakpoint.xl
												? "xl:epic-header-text-sm"
												: mobileBreakpoint === Breakpoint.lg
													? "lg:epic-header-text-sm"
													: "md:epic-header-text-sm"
										}`
						}`}
						ref={bottomBarRef}
					>
						<div className="epic-header-mx-auto epic-header-max-w-screen-xl">
							<div
								className={`epic-header-flex epic-header-items-center epic-header-justify-between epic-header-h-16 ${
									bottomBarItemsGap === ItemsGap.Large
										? "epic-header-gap-6"
										: bottomBarItemsGap === ItemsGap.Medium
											? "epic-header-gap-4"
											: "epic-header-gap-2"
								} epic-header-px-8`}
							>
								<a
									id={`navbar_desktop_big_epic_logo_link`}
									href="/"
									className="epic-header-h-full epic-header-w-fit epic-header-py-3 epic-header-pr-2"
									title={siteIdentityLinkTooltip}
								>
									<img
										src={siteIdentityImageUrl}
										alt=""
										className="epic-header-object-contain epic-header-h-full epic-header-aspect-auto"
									/>
								</a>
								<ul
									role="menubar"
									className={`epic-header-flex epic-header-flex-row epic-header-items-center epic-header-justify-center epic-header-h-full ${
										bottomBarItemsGap === ItemsGap.Large
											? "epic-header-gap-16"
											: bottomBarItemsGap === ItemsGap.Medium
												? "epic-header-gap-6"
												: "epic-header-gap-2"
									}`}
								>
									{desktopBottomItems
										? desktopBottomItems.map(bottomItem => {
												if (bottomItem.subItems) {
													return (
														<BottomNavigationSubMenu
															item={bottomItem}
															key={`nav_bar_bottom_navigation_submenu_${bottomItem.label}`}
															highlightForOpenPage={bottomItem.allowhighlight !== false}
															hoverUnderline={bottomBarHoverUnderline}
														/>
													);
												} else if (bottomItem.href) {
													return (
														<BottomNavigationItem
															item={bottomItem}
															key={`nav_bar_bottom_navigation_item_${bottomItem.label}`}
															highlightForOpenPage={bottomItem.allowhighlight !== false}
															hoverUnderline={bottomBarHoverUnderline}
														/>
													);
												}
											})
										: null}
								</ul>
							</div>
						</div>
					</div>
				) : null}
			</nav>
			<div className={`hidden ${contentBelowTopPadding} ${blockAtBreakpoint}`}></div>
		</>
	);
};
export default DesktopNavBar;

